import React, {useCallback, useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form from "../../base/form";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../base/Tabs";
import {CustomerGiftCardTypeIds} from "../../../../core/constants/enums";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import CustomerAutocomplete from "../../text-fields/autocomplete/customer-autocomplete";
import {OpenInNew, WarningOutlined} from "@mui/icons-material";
import {IconButton} from "@mui/material";
import {Link} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";

const AddGiftCardTabs = {
    refillable: 'Gift Card',
    fixed: 'Coupon (Physical Gift Card)',
}

const schema = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required),
    category: Yup.object().nullable().required(ValidateMessages.required),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref('expiryDate'), ValidateMessages.lessThan('Expiry Date'))
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .nullable(),
    expiryDate: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Activation Date')),
})

const schemaWithoutUser = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required),
    category: Yup.mixed().nullable().required(ValidateMessages.required),
    customer: Yup.mixed().nullable().required(ValidateMessages.required),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref('expiryDate'), ValidateMessages.lessThan('Expiry Date'))
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .nullable(),
    expiryDate: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Activation Date')),
})

const refillableGiftCardSchema = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Number')),
    category: Yup.mixed().nullable().required(ValidateMessages.required),
})

const refillableGiftCardSchemaWithoutUser = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Number')),
    category: Yup.mixed().nullable().required(ValidateMessages.required),
    customer: Yup.mixed().nullable().required(ValidateMessages.required),
})


const CreateGiftCardDialog = ({
                                  open,
                                  setOpen,
                                  onSuccess,
                                  customerId,
                                  onlyFixed = false,
                              }) => {
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(AddGiftCardTabs.fixed);
    const [giftCardCategories, setGiftCardCategories] = useState([]);
    const [loadingGiftCardCategories, setLoadingGiftCardCategories] = useState(false)
    const [giftCardAmounts, setGiftCardAmounts] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Listens for the changes in open, and with each change:
     * if open, resets the selected tab.
     */
    useEffect(() => {
        if (!open)
            return;
        setTab(onlyFixed ? AddGiftCardTabs.fixed : AddGiftCardTabs.refillable)
    }, [onlyFixed, open])

    /**
     * Fetches the list of gift card categories from the server and if the result of the api is successful, sets the
     * list.
     * @type {function(): Promise<void>}
     */
    const getGiftCardCategories = useCallback(async () => {
        setLoadingGiftCardCategories(true);
        const response = await Api.getGiftCardCategories();
        if (!isMounted())
            return;
        setLoadingGiftCardCategories(false);
        if (response?.resultFlag) {
            setGiftCardCategories(response.data);
        }
    }, [isMounted])

    /**
     * Fetches the list of gift card categories from the server and if the result of the api is successful, sets the
     * list.
     * @type {function(): Promise<void>}
     */
    const getGiftCardAmounts = useCallback(async () => {
        const response = await Api.getGiftCardAmounts();
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setGiftCardAmounts(response.data?.map(e => ({title: formatMoney(e), id: e})));
        }
    }, [isMounted])

    /**
     * Loads the gift card categories as soon as the component mounts
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        void getGiftCardCategories();
        void getGiftCardAmounts();
    }, [getGiftCardAmounts, getGiftCardCategories, open])

    /**
     * Adds a new gift card by calling the api server and if the result was successful, calls the onSuccess callback
     * with the newly created gift card as its argument.
     * @param values {any} from values
     */
    const addGiftCard = useCallback(async (values) => {
        setLoading(true);
        const _customerId = customerId ?? values?.customer?.id;
        const forApi = {
            isActive: true,
            amount: parseFloat(`${values.amount}`),
            expireDate: values?.expiryDate ? DateUtils.UTCToServer(values?.expiryDate).format(DateUtils.withoutOffsetFormat) : null,
            activeDate: values?.startDate ? DateUtils.UTCToServer(values?.startDate).format(DateUtils.withoutOffsetFormat) : null,
            lockForCustomer: _customerId,
            category: values.category,
            type: {id: tab === AddGiftCardTabs.fixed ? CustomerGiftCardTypeIds.giftCard : CustomerGiftCardTypeIds.refillable},
        }
        const response = await Api.createGiftCard(forApi)
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            onSuccess({
                ...response.data,
                category: values.category,
                currentBalance: parseFloat(values.amount),
                lockedCustomerInfo: values.customer,
            });
            setOpen(false);
        }
    }, [customerId, isMounted, onSuccess, setOpen, tab])

    /**
     * Changes the tab of the page between orders and items of orders
     * @type {function(*, *): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setTab(newValue);
    }, []);


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Create Gift Card / Coupon
                    </p>
                </Row>
                <Divider className='divider'/>
                {
                    !onlyFixed &&
                    <>
                        <div className='my-1 w-100'/>
                        <div className='w-100 flex-grow-1'>
                            <Tabs value={tab} onChange={onTabChange}>
                                <Tab value={AddGiftCardTabs.refillable} label={AddGiftCardTabs.refillable}/>
                                <Tab value={AddGiftCardTabs.fixed} label={AddGiftCardTabs.fixed}/>
                            </Tabs>
                        </div>
                    </>
                }
                <div className='my-4 w-100'/>
                {
                    tab === AddGiftCardTabs.refillable &&
                    <RefillableGiftCards
                        addGiftCard={addGiftCard}
                        loading={loading}
                        withCustomer={!!customerId}
                        giftCardCategories={giftCardCategories}
                        loadingGiftCardCategories={loadingGiftCardCategories}
                        setOpen={setOpen}
                        open={open}
                    />
                }
                {
                    tab === AddGiftCardTabs.fixed &&
                    <FixedGiftCards
                        addGiftCard={addGiftCard}
                        loading={loading}
                        withCustomer={!!customerId}
                        giftCardAmounts={giftCardAmounts}
                        giftCardCategories={giftCardCategories}
                        loadingGiftCardCategories={loadingGiftCardCategories}
                        setOpen={setOpen}
                        open={open}
                    />
                }
            </Row>
        </Dialog>
    );
}

const RefillableGiftCards = ({
                                 addGiftCard,
                                 loading,
                                 withCustomer,
                                 giftCardCategories,
                                 loadingGiftCardCategories,
                                 setOpen,
                             }) => {
    const validate = MuiRFF.makeValidate(withCustomer ? refillableGiftCardSchema : refillableGiftCardSchemaWithoutUser);
    const required = MuiRFF.makeRequired(withCustomer ? refillableGiftCardSchema : refillableGiftCardSchemaWithoutUser);

    return (
        <Form
            className='w-100'
            onSubmit={addGiftCard}
            validate={validate}
            render={({values, form}) => {
                return (
                    <Row className='no-gutters justify-content-around w-100'>
                        <Row className='no-gutters justify-content-around w-100 mx-4'>
                            <Col xs={12} className={'mb-4 px-1'}>
                                <OutlinedInputForm
                                    label={'Amount'}
                                    value={values?.amount?.toString() ?? ''}
                                    name={'amount'}
                                    type={'number'}
                                    required={!!required.amount}
                                    onChange={(e) => form.change('amount', e.target.value ? parseFloat(e.target.value) : undefined)}
                                />
                            </Col>
                            <Col xs={12} className={'mb-4 px-1'}>
                                <OutlinedDropdownForm
                                    label={'Category'}
                                    name={`category`}
                                    required={required.category}
                                    data={giftCardCategories?.map(e => ({
                                        value: e,
                                        label: e.name,
                                        id: e.id
                                    })) ?? []}
                                />
                            </Col>
                            {
                                !loadingGiftCardCategories && giftCardCategories?.length === 0 &&
                                <Col xs={12} className='px-2 mb-3'>
                                    <p className={'text-sm text-color-3 text-center'}>
                                        <WarningOutlined
                                            color={'warning'}
                                            sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                        />
                                        There are no categories to select. Please create a category first.
                                        <IconButton
                                            color={'paper.primary'}
                                            component={Link}
                                            to={routes.settings.giftCards.categories}
                                            target={'_blank'}
                                            sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                            onClick={() => setOpen(false)}
                                        >
                                            <OpenInNew/>
                                        </IconButton>
                                    </p>
                                </Col>
                            }
                            {
                                !withCustomer &&
                                <Col xs={12} className={'mb-4 px-1'}>
                                    <CustomerAutocomplete
                                        form
                                        name={'customer'}
                                        fullWidth
                                        required={required.customer}
                                        label={'Customer'}
                                    />
                                </Col>
                            }

                        </Row>
                        <div className='my-2 w-100'/>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Row className='no-gutters justify-content-between align-items-start w-100'>
                                <Button
                                    disabled={loading}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    Close
                                </Button>
                                <Button
                                    disabled={loading}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={'submit'}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {loading ? 'loading...' : 'Add'}
                                </Button>
                            </Row>
                        </Row>
                    </Row>
                );
            }}
        />
    );
}


const FixedGiftCards = ({
                            addGiftCard,
                            loading,
                            withCustomer,
                            giftCardAmounts,
                            giftCardCategories,
                            loadingGiftCardCategories,
                            setOpen,
                        }) => {
    const validate = MuiRFF.makeValidate(withCustomer ? schema : schemaWithoutUser);
    const required = MuiRFF.makeRequired(withCustomer ? schema : schemaWithoutUser);

    return (
        <Form
            className='w-100'
            onSubmit={addGiftCard}
            validate={validate}
            render={({values}) => {
                return (
                    <Row className='no-gutters justify-content-around w-100'>
                        <Row className='no-gutters justify-content-around w-100 mx-4'>
                            <Col xs={12} className={'mb-4 px-1'}>
                                <OutlinedDropdownForm
                                    label={'Amount'}
                                    name={'amount'}
                                    required={required.amount}
                                    data={giftCardAmounts?.map(e => ({
                                        value: e.id,
                                        label: e.title,
                                        id: e.id
                                    })) ?? []}
                                />
                            </Col>
                            <Col xs={12} className={'mb-4 px-1'}>
                                <OutlinedDropdownForm
                                    label={'Category'}
                                    name={`category`}
                                    required={required.category}
                                    data={giftCardCategories?.map(e => ({
                                        value: e,
                                        label: e.name,
                                        id: e.id
                                    })) ?? []}
                                />
                            </Col>
                            {
                                !loadingGiftCardCategories && giftCardCategories?.length === 0 &&
                                <Col xs={12} className='px-2 mb-3'>
                                    <p className={'text-sm text-color-3 text-center'}>
                                        <WarningOutlined
                                            color={'warning'}
                                            sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                        />
                                        There are no categories to select. Please create a category first.
                                        <IconButton
                                            color={'paper.primary'}
                                            component={Link}
                                            to={routes.settings.giftCards.categories}
                                            target={'_blank'}
                                            sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                            onClick={() => setOpen(false)}
                                        >
                                            <OpenInNew/>
                                        </IconButton>
                                    </p>
                                </Col>
                            }
                            <Col xs={12} className={'mb-4 px-1'}>
                                <MuiRFF.DatePicker
                                    name={"startDate"}
                                    label={"Activation Date (optional)"}
                                    required={required.startDate}
                                    maxDate={values.expiryDate}
                                    disablePast
                                />
                            </Col>
                            <Col xs={12} className={'mb-4 px-1'}>
                                <MuiRFF.DatePicker
                                    name={"expiryDate"}
                                    label={"Expiry Date (optional)"}
                                    required={required.expiryDate}
                                    minDate={values.startDate}
                                    disablePast
                                />
                            </Col>
                            {
                                !withCustomer &&
                                <Col xs={12} className={'mb-4 px-1'}>
                                    <CustomerAutocomplete
                                        form
                                        name={'customer'}
                                        fullWidth
                                        required={required.customer}
                                        label={'Customer'}
                                    />
                                </Col>
                            }
                        </Row>
                        <div className='my-2 w-100'/>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Row className='no-gutters justify-content-between align-items-start w-100'>
                                <Button
                                    disabled={loading}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {loading ? 'loading...' : 'Close'}
                                </Button>
                                <Button
                                    disabled={loading}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={'submit'}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {loading ? 'loading...' : 'Add'}
                                </Button>
                            </Row>
                        </Row>
                    </Row>
                );
            }}
        />
    );
}


export default CreateGiftCardDialog;
